import React, { useState, useCallback } from 'react';
import { User, Bot, Copy, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { SpeakerButton } from './SpeakerButton';
import { useAudioPlayback } from '../hooks/useAudioPlayback';

// Use relative paths for ngrok/proxy, absolute for direct localhost access
const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
const API_BASE_URL = process.env.REACT_APP_API_URL || (isLocalhost ? 'http://localhost:5000' : '');

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
  showCalendar?: boolean;
  audioData?: string; // Base64-encoded audio
  debugReasoning?: boolean; // Whether to show AI reasoning (debug mode)
  reasoningSummary?: string; // AI reasoning summary (from GPT-5 models)
}

interface MessageBubbleProps {
  message: Message;
  isDarkMode: boolean;
  showLoadingDots?: boolean;
  isStreaming?: boolean;
  onCalendarClick?: () => void;
  customerSlug?: string;
  agentSlug?: string;
}

// Renders completed **bold** and *italic* spans during streaming; incomplete markers stay literal.
const renderStreamingContent = (text: string): React.ReactNode => {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*\n]+\*)/g);
  return (
    <span className="whitespace-pre-wrap">
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**') && part.length > 4)
          return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
        if (part.startsWith('*') && part.endsWith('*') && part.length > 2)
          return <em key={i}>{part.slice(1, -1)}</em>;
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </span>
  );
};

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isDarkMode, showLoadingDots = false, isStreaming = false, onCalendarClick, customerSlug, agentSlug }) => {
  const isUser = message.sender === 'user';
  const { isPlaying, play, stop } = useAudioPlayback();
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // Handle copy button click
  const handleCopyClick = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(message.text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  }, [message.text]);

  // Handle speaker button click - play stored audio or generate on-demand
  const handleSpeakerClick = useCallback(async () => {
    if (isPlaying) {
      stop();
      return;
    }

    // If audio data is already stored, play it
    if (message.audioData) {
      play(message.audioData);
      return;
    }

    // Otherwise, generate audio on-demand using TTS
    if (!message.text || isGeneratingAudio) return;

    setIsGeneratingAudio(true);
    try {
      const adminToken = sessionStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/voice/speak`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
        },
        body: JSON.stringify({
          text: message.text,
          voice: 'nova',
          customer_slug: customerSlug,
          agent_slug: agentSlug,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.audio_data) {
          play(result.audio_data);
        }
      } else {
        let errorDetail = '';
        try {
          const err = await response.json();
          errorDetail = err?.error ? ` - ${err.error}` : '';
        } catch {
          // Ignore parse failures for non-JSON error responses
        }
        console.error(`TTS API error: ${response.status}${errorDetail}`);
      }
    } catch (err) {
      console.error('Error generating audio:', err);
    } finally {
      setIsGeneratingAudio(false);
    }
  }, [agentSlug, customerSlug, isPlaying, play, stop, message.audioData, message.text, isGeneratingAudio]);

  return (
    <div className={`message-bubble-row flex ${isUser ? 'justify-end' : 'justify-start'} px-2 sm:px-0`}>
      <div className={`message-bubble-shell flex gap-2 sm:gap-3 max-w-xs sm:max-w-2xl lg:max-w-3xl ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
        {/* Avatar */}
        <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isUser 
            ? 'bg-[#ed7203]'
            : isDarkMode 
              ? 'bg-gray-700' 
              : 'bg-gray-200'
        }`}>
          {isUser ? (
            <User size={14} className="text-white sm:hidden" />
          ) : (
            <Bot size={14} className={`sm:hidden ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
          )}
          {isUser ? (
            <User size={16} className="text-white hidden sm:block" />
          ) : (
            <Bot size={16} className={`hidden sm:block ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
          )}
        </div>
        
        {/* Message Content */}
        <div className={`message-bubble-content rounded-xl sm:rounded-2xl p-3 sm:p-4 text-sm sm:text-base ${
          isUser
            ? 'bg-[#ed7203] text-white'
            : isDarkMode
              ? 'bg-gray-800 text-gray-100'
              : 'bg-gray-100 text-gray-900'
        } ${isUser ? 'rounded-br-md' : 'rounded-bl-md'}`}>
          {showLoadingDots ? (
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-current rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          ) : (
            <div className="message-bubble-prose prose prose-sm max-w-none break-words">
              {isStreaming ? (
                renderStreamingContent(message.text)
              ) : (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    /* Use <div> instead of <p> to avoid validateDOMNesting errors
                       when markdown content contains block-level elements */
                    p: ({ children }) => <div className="mb-2 last:mb-0">{children}</div>,
                    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                    em: ({ children }) => <em className="italic">{children}</em>,
                    a: ({ href, children }) => (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`underline hover:no-underline transition-colors ${
                          isDarkMode ? 'text-orange-300 hover:text-orange-200' : 'text-[#c75f02] hover:text-[#8a4300]'
                        }`}
                      >
                        {children}
                      </a>
                    ),
                    ul: ({ children }) => <ul className="list-disc pl-5 mb-2 space-y-1">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 space-y-1">{children}</ol>,
                    li: ({ children }) => <li className="pl-0">{children}</li>,
                    h1: ({ children }) => <h1 className="text-lg font-bold mb-2">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-base font-bold mb-2">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-sm font-bold mb-1">{children}</h3>,
                    code: ({ children, className }) => {
                      if (!className) {
                        return (
                          <code className={`px-1 py-0.5 rounded text-xs font-mono ${
                            isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-800'
                          }`}>
                            {children}
                          </code>
                        );
                      }
                      return (
                        <code className={`block p-3 rounded-md text-xs font-mono overflow-x-auto ${
                          isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {children}
                        </code>
                      );
                    },
                    blockquote: ({ children }) => (
                      <blockquote className={`border-l-4 pl-4 my-2 italic ${
                        isDarkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-600'
                      }`}>
                        {children}
                      </blockquote>
                    )
                  }}
                >
                  {message.text}
                </ReactMarkdown>
              )}
            </div>
          )}
          
          {/* Timestamp and Speaker Button */}
          <div className={`flex items-center gap-2 mt-2 text-xs ${
            isUser
              ? 'text-orange-100'
              : isDarkMode
                ? 'text-gray-400'
                : 'text-gray-500'
          }`}>
            <span>
              {message.timestamp.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
            {/* Copy and Speaker buttons for assistant messages */}
            {!isUser && message.text && !showLoadingDots && (
              <>
                {/* Copy button */}
                <button
                  onClick={handleCopyClick}
                  className={`p-1.5 rounded-full transition-all duration-200 ${
                    isCopied
                      ? 'text-green-500'
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                  }`}
                  title={isCopied ? 'Copied!' : 'Copy message'}
                >
                  {isCopied ? <Check size={16} /> : <Copy size={16} />}
                </button>
                {/* Speaker button */}
                <SpeakerButton
                  isPlaying={isPlaying}
                  isLoading={isGeneratingAudio}
                  onClick={handleSpeakerClick}
                  disabled={false}
                />
              </>
            )}
          </div>
          
          {/* Calendar indicator */}
          {message.showCalendar && (
            <button
              onClick={onCalendarClick}
              className={`text-xs mt-1 hover:underline cursor-pointer transition-colors ${
                isUser
                  ? 'text-orange-100 hover:text-white'
                  : isDarkMode
                    ? 'text-orange-300 hover:text-orange-200'
                    : 'text-[#c75f02] hover:text-[#8a4300]'
              }`}
            >
              Kalender verfügbar
            </button>
          )}

          {/* Reasoning Summary (Debug Mode Only) */}
          {!isUser && message.debugReasoning && message.reasoningSummary && (
            <details className={`mt-2 text-xs ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              <summary className={`cursor-pointer hover:underline select-none ${
                isDarkMode ? 'text-orange-300' : 'text-[#c75f02]'
              }`}>
                Show AI Reasoning
              </summary>
              <div className={`mt-1 p-2 rounded text-xs font-mono whitespace-pre-wrap ${
                isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
              }`}>
                {message.reasoningSummary}
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(MessageBubble);
